import React from "react";
import {
  Box,
  List,
  ListItemButton,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

import { BadgeType, DivergeInfo, MeetingInfoBadge } from "./MeetingInfoBadge";
import { Meeting, isMissingData } from "../../../model/meeting";
import { hostLabel } from "../../../model/host";
import {
  getDateString,
  getTimeString,
  hasApparentOverride,
  stableKeyOf,
} from "../helpers";

export interface MeetingListItemProps {
  meeting: Meeting;
  /** Partner that scoped this meeting as partner-only, when any. */
  privatePartnerName?: string;
  /** Every partner that marked this meeting as preferred (one-to-many). */
  preferredPartnerNames?: string[];
}

const MeetingListItem: React.FC<MeetingListItemProps> = ({
  meeting,
  privatePartnerName,
  preferredPartnerNames,
}) => {
  return (
    <Stack
      direction="row"
      gap={{ xs: 2, sm: 5 }}
      width="100%"
      alignItems="center"
      padding={{ xs: "12px", sm: "20px" }}
    >
      <Stack flex={1}>
        <Typography
          variant="caption"
          style={{ color: "gray", marginLeft: "-5px" }}
        >
          {getDateString(meeting.start)}
        </Typography>
        <Stack
          direction="row"
          gap={0.75}
          alignItems="center"
          minWidth={{ xs: 0, sm: 300 }}
          justifyContent="flex-start"
        >
          <Typography variant="body1" fontWeight="bold">
            {meeting.title}
          </Typography>
          {hasApparentOverride(meeting) && <DivergeInfo />}
        </Stack>
        <Typography variant="body2" fontWeight="bold" style={{ color: "gray" }}>
          {getTimeString(meeting.start, meeting.end)}
        </Typography>
        <Typography variant="caption" style={{ color: "gray" }}>
          {meeting.host ? hostLabel(meeting.host) : "Host unset"}
        </Typography>
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          justifyContent="flex-start"
          marginTop="15px"
          marginLeft="-8px"
        >
          {meeting.metadata.meetingType && (
            <MeetingInfoBadge
              type={BadgeType.meetingType}
              meetingTypeLabel={meeting.metadata.meetingType}
            />
          )}
          {meeting.recurringMeeting && (
            <MeetingInfoBadge type={BadgeType.recurring} />
          )}
        </Stack>
      </Stack>
      <Stack gap={1} flex={1} alignItems="flex-end">
        {!meeting.metadata.memberVisible && (
          <MeetingInfoBadge type={BadgeType.notVisible} />
        )}
        {isMissingData(meeting) && (
          <MeetingInfoBadge type={BadgeType.missingData} />
        )}
        {privatePartnerName && (
          <MeetingInfoBadge
            type={BadgeType.partnerPrivate}
            partnerLabel={privatePartnerName}
          />
        )}
        {!privatePartnerName && preferredPartnerNames?.length ? (
          <MeetingInfoBadge
            type={BadgeType.partnerPreferred}
            partnerLabel={preferredPartnerNames.join(", ")}
          />
        ) : null}
      </Stack>
    </Stack>
  );
};

interface MeetingListProps {
  meetings: Meeting[];
  onSelectMeeting: (mId: string) => void;
  selectedMeetingId?: string;
  /** Gcal event id → partner name for partner-only meetings. */
  privateByGcalId?: Map<string, string>;
  /** Stable session key → names of every partner that marked it preferred. */
  preferredNamesByKey?: Map<string, string[]>;
}

export const MeetingList: React.FC<MeetingListProps> = ({
  meetings,
  onSelectMeeting,
  selectedMeetingId,
  privateByGcalId,
  preferredNamesByKey,
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflow: "auto",
      }}
    >
      <List>
        {meetings.map((md) => {
          return (
            <React.Fragment key={md.id}>
              <ListItemButton
                onClick={() => onSelectMeeting(md.id)}
                selected={md.id === selectedMeetingId}
              >
                <MeetingListItem
                  meeting={md}
                  privatePartnerName={privateByGcalId?.get(md.id)}
                  preferredPartnerNames={preferredNamesByKey?.get(
                    stableKeyOf(md),
                  )}
                />
              </ListItemButton>
              <Divider />
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};
